/**
 * Eneto UTM Persistence
 * ---------------------
 * Loest das Problem, dass UTM-Parameter manuell von Link zu Link
 * weitergereicht werden muessen (Landingpage -> Pop-up -> Formular).
 *
 * FIX 2026-08-13 (UTM-Passthrough-Bug): Es gibt jetzt ZWEI GETRENNTE
 * SPEICHER statt einem einzigen 30-Tage-Speicher:
 *
 * 1. SESSION-SPEICHER (sessionStorage, "eneto_utm_session")
 *    Wird verwendet, um [data-utm-passthrough]-Elemente (Formulare UND
 *    Links, z.B. der Header-CTA-Button) aktiv mit UTM-Parametern zu
 *    befuellen. Lebt nur fuer die Dauer des Browser-Tabs. Kommt ein
 *    Nutzer Tage spaeter ohne neuen UTM-Parameter zurueck (neuer Tab /
 *    neue Session), ist dieser Speicher leer -> der Button "klebt" nicht
 *    mehr dauerhaft an einer alten Kampagne.
 *
 * 2. HINTERGRUND-SPEICHER (localStorage, "eneto_utm_attribution")
 *    Rein fuer spaetere Attribution / Reporting / Provisionsabrechnung
 *    gedacht (z.B. Auslesen bei Checkout/Conversion ueber
 *    EnetoUTM.getAttribution()). Laeuft nach 14 Tagen ab. Wird NIE aktiv
 *    verwendet, um Buttons/Links/Formulare zu befuellen - nur zum
 *    Nachschlagen "woher kam dieser Nutzer urspruenglich" ueber die
 *    Session hinaus.
 *
 * BEIDE Speicher folgen Last-Click: Sobald ein neuer utm_* Parameter in
 * der URL auftaucht, wird der bestehende Wert ueberschrieben (nicht nur
 * beim allerersten Besuch, wie zuvor). Reiner Direct-Traffic (keine neuen
 * UTM-Parameter in der URL, z.B. interne Navigation) loescht NICHTS und
 * verlaengert auch KEINE Gueltigkeit - der bestehende Wert bleibt einfach
 * unangetastet stehen, bis er entweder ueberschrieben wird (neuer
 * Touchpoint) oder ablaeuft (Hintergrundspeicher) bzw. die Session endet
 * (Session-Speicher).
 *
 * Seiten mit fest hinterlegtem UTM-Zweig (Infranken, Home&Smart, Svea)
 * nutzen in header.liquid eigene {% elsif request.path == ... %}-Zweige
 * OHNE data-utm-passthrough und werden von diesem Mechanismus nie
 * beruehrt.
 *
 * Nutzung im Theme:
 *   <script src="utm-persistence.js"></script>
 *   <form data-utm-passthrough> ... </form>
 *   <a data-utm-passthrough href="..."> ... </a>
 *
 * Das Script laeuft automatisch bei DOMContentLoaded und:
 *   1. liest utm_* aus der aktuellen URL
 *   2. ueberschreibt damit Session- UND Hintergrundspeicher (Last-Click),
 *      sofern neue Parameter vorhanden sind
 *   3. befuellt alle [data-utm-passthrough] Formulare/Links ausschliesslich
 *      aus dem SESSION-Speicher
 *
 * ERWEITERUNG 2026-09-11 (automatischer Berater-Passthrough):
 *   4. Ein delegierter Klick-Handler befuellt zusaetzlich JEDEN Link, dessen
 *      Ziel EXAKT der generische Berater-Pfad '/pages/klimaanlagen-berater'
 *      ist - auch ohne data-utm-passthrough und auch bei dynamisch
 *      eingefuegten Links. Der Vergleich ist ein exakter Pfadvergleich, kein
 *      Substring-Match: '/pages/klimaanlagen-berater-infranken' und
 *      '/pages/klimaanlagen-berater-hs' sind andere Pfade und werden bewusst
 *      NICHT erfasst.
 *   5. Gelesen wird bevorzugt der vollstaendigere Attributionssatz, den Code 1
 *      in layout/theme.liquid unter 'heyflow_utms_session_v2' ablegt (inkl.
 *      gclid/fbclid/gad_source sowie der google/cpc-Ableitung), mit Fallback
 *      auf den eigenen Session-Speicher. Geschrieben wird dort nichts.
 *   6. Alle Merges sind NICHT-DESTRUKTIV (wie Code 2 in layout/theme.liquid):
 *      ein bereits auf dem Ziel-Link vorhandener Parameter gewinnt immer.
 *      Damit bleiben fest hinterlegte Affiliate-UTMs unangetastet.
 *
 * ZWEI SCHUTZREGELN, die AUSSCHLIESSLICH fuer die automatische Erkennung
 * gelten (das explizite [data-utm-passthrough] bleibt davon unberuehrt):
 *   a) utm_source-Guard: traegt das Ziel bereits eine nicht-leere utm_source,
 *      wird der Link GAR NICHT angefasst. Bewusst gesetzte Links (Infranken,
 *      Svea, onsite) bleiben damit byte-fuer-byte unveraendert - es wird auch
 *      kein fehlender Parameter wie gclid/fbclid/gad_source/utm_term ergaenzt.
 *   b) Host-Guard: dekoriert wird nur ein Ziel auf einem bekannten Store-Host
 *      (siehe STORE_HOSTNAMES) oder auf dem Hostnamen der aktuellen Seite.
 *      Ein fremdes Host mit zufaellig gleichem Pfad wird nie angefasst.
 */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) {
    module.exports = factory(); // Node / Tests
  } else {
    root.EnetoUTM = factory(); // Browser
  }
}(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  var BACKGROUND_KEY = 'eneto_utm_attribution';
  var SESSION_KEY = 'eneto_utm_session';
  var UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
  var BACKGROUND_MAX_AGE_MS = 14 * 24 * 60 * 60 * 1000; // 14 Tage Gueltigkeit fuer Reporting/Attribution

  // Nur LESEN: Code 1 in layout/theme.liquid schreibt hier den vollstaendigeren
  // Satz (inkl. Google-/Meta-Keys). Struktur dort: { values: {...},
  // capturedAt: n } - gleiche flache Map wie unser { params: {...} }, nur mit
  // anderem Property-Namen. Nichts davon wird umbenannt oder geloescht.
  var HEYFLOW_SESSION_KEY = 'heyflow_utms_session_v2';
  // Zusaetzlich die Meta-/Google-Ride-Along-Keys, die Code 1 nur zusammen mit einem
  // seiner Trigger-Keys speichert. UTM_KEYS (eigene Erfassung) bleibt
  // bewusst unveraendert.
  var PASSTHROUGH_KEYS = UTM_KEYS.concat([
    'gclid', 'fbclid', 'gad_source',
    'utm_id', 'campaign_id', 'adset_id', 'ad_id', 'placement', 'site_source_name',
    'utm_creative', 'gbraid', 'wbraid', 'gad_campaignid'
  ]);

  // Der EXAKTE generische Berater-Pfad. Affiliate-Ziele (-infranken, -hs) sind
  // andere Pfade und matchen deshalb nie.
  var GENERIC_BERATER_PATH = '/pages/klimaanlagen-berater';

  // Erlaubte Ziel-Hostnamen fuer die AUTOMATISCHE Erkennung. Exakter Vergleich,
  // KEIN Substring-Match. Zusaetzlich gilt immer der Hostname der aktuellen
  // Seite, damit Shopify-Preview-/Testumgebungen mit abgedeckt sind.
  var STORE_HOSTNAMES = ['eneto.com', 'www.eneto.com', 'hh73ry-7e.myshopify.com'];

  function parseIncomingUTM(search) {
    var params = new URLSearchParams(search || '');
    var result = {};
    UTM_KEYS.forEach(function (key) {
      var value = params.get(key);
      if (value) result[key] = value;
    });
    return result;
  }

  function hasAny(obj) {
    return !!obj && Object.keys(obj).length > 0;
  }

  function safeGet(storage, key) {
    try {
      return storage.getItem(key);
    } catch (e) {
      return null;
    }
  }

  function safeSet(storage, key, value) {
    try {
      storage.setItem(key, value);
    } catch (e) {
      /* storage nicht verfuegbar (z.B. Privacy-Mode) - fail silent */
    }
  }

  function safeRemove(storage, key) {
    try {
      storage.removeItem(key);
    } catch (e) { /* ignore */ }
  }

  function isExpired(data, now, maxAgeMs) {
    if (!data || !data.capturedAt) return true;
    return (now || Date.now()) - data.capturedAt > maxAgeMs;
  }

  // --- Hintergrundspeicher (localStorage, 14 Tage, nur fuer Reporting/Attribution) ---

  function readBackground(storage, now) {
    if (!storage) return null;
    try {
      var raw = safeGet(storage, BACKGROUND_KEY);
      if (!raw) return null;
      var data = JSON.parse(raw);
      if (isExpired(data, now, BACKGROUND_MAX_AGE_MS)) {
        safeRemove(storage, BACKGROUND_KEY);
        return null;
      }
      return data;
    } catch (e) {
      return null;
    }
  }

  function writeBackground(storage, incoming, href, now) {
    if (!storage) return;
    var data = { params: incoming, capturedAt: now, firstUrl: href || null };
    safeSet(storage, BACKGROUND_KEY, JSON.stringify(data));
  }

  // --- Session-Speicher (sessionStorage, aktiv fuer Passthrough-Elemente) ---

  function readSession(storage) {
    if (!storage) return null;
    try {
      var raw = safeGet(storage, SESSION_KEY);
      if (!raw) return null;
      var data = JSON.parse(raw);
      return data && hasAny(data.params) ? data : null;
    } catch (e) {
      return null;
    }
  }

  function writeSession(storage, incoming, href, now) {
    if (!storage) return;
    var data = { params: incoming, capturedAt: now, firstUrl: href || null };
    safeSet(storage, SESSION_KEY, JSON.stringify(data));
  }

  /**
   * Erfasst UTM-Parameter aus der aktuellen URL. Last-Click: ist ein neuer
   * utm_* Parameter in der URL vorhanden, ueberschreibt er IMMER den
   * bestehenden Wert (Session- UND Hintergrundspeicher). Reiner
   * Direct-Traffic (keine UTM-Parameter in der URL) fasst beide Speicher
   * nicht an - kein Loeschen, keine Laufzeit-Verlaengerung.
   */
  function capture(options) {
    options = options || {};
    var localStore = options.localStorage;
    var sessionStore = options.sessionStorage;
    var now = options.now || Date.now();
    var incoming = parseIncomingUTM(options.search);

    if (hasAny(incoming)) {
      writeBackground(localStore, incoming, options.href, now);
      writeSession(sessionStore, incoming, options.href, now);
      return incoming;
    }
    return null;
  }

  /**
   * Attribution fuer Reporting/Provisionsabrechnung (14 Tage,
   * sitzungsuebergreifend). Wird NICHT verwendet, um Buttons/Links/
   * Formulare zu befuellen - nur zum Nachschlagen der urspruenglichen
   * Herkunft (z.B. beim Checkout an GTM/dataLayer uebergeben).
   */
  function getAttribution(localStore, now) {
    var stored = readBackground(localStore, now);
    return (stored && stored.params) || {};
  }

  /**
   * Aktive Passthrough-Werte fuer die aktuelle Session (Landingpage ->
   * Pop-up -> Formular/Button). Leer, sobald die Session ohne neuen
   * UTM-Touchpoint beginnt (z.B. neuer Tab an einem anderen Tag).
   */
  function getSessionAttribution(sessionStore) {
    var stored = readSession(sessionStore);
    return (stored && stored.params) || {};
  }

  /**
   * Liest den Session-Satz, den Code 1 in layout/theme.liquid unter
   * 'heyflow_utms_session_v2' ablegt. Dort heisst das Feld 'values' (bei uns
   * 'params'), der Inhalt ist in beiden Faellen eine flache Key->String-Map.
   */
  function readHeyflowSession(sessionStore) {
    if (!sessionStore) return {};
    try {
      var record = JSON.parse(safeGet(sessionStore, HEYFLOW_SESSION_KEY) || 'null');
      return (record && record.values) || {};
    } catch (e) {
      return {};
    }
  }

  /**
   * Die Attributionsquelle fuer alle Passthrough-Vorgaenge. Bevorzugt den
   * vollstaendigeren Heyflow-Satz, faellt auf den eigenen 5-Key-
   * Session-Speicher zurueck. Dadurch funktioniert Passthrough auch fuer
   * gclid-only Google-Ads-Klicks, bei denen 'eneto_utm_session' leer bleibt.
   */
  function getPassthroughAttribution(sessionStore) {
    var source = readHeyflowSession(sessionStore);
    if (!hasAny(source)) source = getSessionAttribution(sessionStore);
    var result = {};
    PASSTHROUGH_KEYS.forEach(function (key) {
      if (source[key]) result[key] = source[key];
    });
    return result;
  }

  function clearAttribution(localStore) {
    safeRemove(localStore, BACKGROUND_KEY);
  }

  function clearSession(sessionStore) {
    safeRemove(sessionStore, SESSION_KEY);
  }

  function appendParamsToUrl(url, params, baseHref) {
    try {
      var base = baseHref || (typeof location !== 'undefined' ? location.href : undefined);
      var u = new URL(url, base);
      Object.keys(params).forEach(function (k) {
        // Nicht-destruktiver Merge, identisch zu layout/theme.liquid Code 2:
        // ein bereits auf dem Ziel-Link vorhandener Parameter gewinnt immer.
        // Das schuetzt die fest hinterlegten Affiliate-UTMs (Infranken,
        // Home&Smart, Svea, Giga) und macht den Aufruf idempotent.
        if (params[k] && !u.searchParams.get(k)) {
          u.searchParams.set(k, params[k]);
        }
      });
      return u.toString();
    } catch (e) {
      return url;
    }
  }

  function normalizeBeraterPath(pathname) {
    return String(pathname || '').toLowerCase().replace(/\/+$/, '') || '/';
  }

  /**
   * Interne Ziel-Absicherung fuer die automatische Erkennung: ein fremdes
   * Host mit zufaellig gleichem Pfad darf NICHT dekoriert werden. Vergleich
   * per exakter Hostname-Gleichheit gegen STORE_HOSTNAMES plus den Hostnamen
   * der aktuellen Seite (Shopify-Preview / Test). Kein Substring-Match.
   */
  function isStoreHostname(hostname, baseHref) {
    var host = String(hostname || '').toLowerCase();
    if (!host) return false;
    if (STORE_HOSTNAMES.indexOf(host) !== -1) return true;
    try {
      var base = baseHref || (typeof location !== 'undefined' ? location.href : undefined);
      if (base && String(new URL(base).hostname).toLowerCase() === host) return true;
    } catch (e) { /* ignore */ }
    return false;
  }

  /**
   * Traegt das Ziel bereits eine nicht-leere utm_source? Dann ist es ein
   * bewusst gesetzter Link (Infranken, Svea, onsite) und wird von der
   * automatischen Erkennung GAR NICHT angefasst.
   */
  function hasExistingUtmSource(rawHref, baseHref) {
    try {
      var base = baseHref || (typeof location !== 'undefined' ? location.href : undefined);
      var value = new URL(rawHref, base).searchParams.get('utm_source');
      return !!(value && value.replace(/^\s+|\s+$/g, ''));
    } catch (e) {
      return false;
    }
  }

  /**
   * EXAKTER Pfadvergleich - ausdruecklich KEIN Substring-Match.
   * Erfasst wird nur '/pages/klimaanlagen-berater' (relativ, absolut auf
   * eneto.com, mit oder ohne Trailing Slash, mit beliebigem Query/Hash).
   * NICHT erfasst werden:
   *   - '/pages/klimaanlagen-berater-infranken' und '-hs' (andere Pfade)
   *   - '/page/...' (Singular-Variante auf der H&S-Seite)
   *   - leere hrefs und reine Anker ('' , '#...') -> der bestehende
   *     Reload-Bug wird bewusst nicht in eine UTM-URL verwandelt
   *   - Nicht-http(s)-Schemata (shopify:, mailto:, tel:)
   *   - fremde Hosts mit zufaellig gleichem Pfad (z.B. example.com)
   */
  function isGenericBeraterHref(rawHref, baseHref) {
    var trimmed = String(rawHref == null ? '' : rawHref).replace(/^\s+|\s+$/g, '');
    if (!trimmed || trimmed.charAt(0) === '#') return false;
    try {
      var base = baseHref || (typeof location !== 'undefined' ? location.href : undefined);
      var u = new URL(trimmed, base);
      if (u.protocol !== 'http:' && u.protocol !== 'https:') return false;
      if (!isStoreHostname(u.hostname, base)) return false;
      return normalizeBeraterPath(u.pathname) === GENERIC_BERATER_PATH;
    } catch (e) {
      return false;
    }
  }

  /**
   * Befuellt einen generischen Berater-Link zum KLICKZEITPUNKT. Deckt damit
   * sowohl beim Laden vorhandene als auch spaeter eingefuegte Links ab, denn
   * geklickt werden kann nur bereits vorhandenes DOM. Bewertet wird das
   * ATTRIBUT href (nicht anchor.href), weil href="" sonst als aktuelle Seite
   * aufgeloest wuerde. Elemente mit data-utm-passthrough werden hier
   * uebersprungen - die erledigt applyToDocument.
   */
  function decorateGenericBeraterAnchor(anchor, sessionStore, loc) {
    if (!anchor || !anchor.getAttribute) return false;
    if (anchor.hasAttribute('data-utm-passthrough')) return false;
    var rawHref = anchor.getAttribute('href');
    var baseHref = (loc && loc.href) || undefined;
    if (!isGenericBeraterHref(rawHref, baseHref)) return false;
    // AFFILIATE-SCHUTZ (gilt nur hier, nicht fuer [data-utm-passthrough]):
    // traegt das Ziel schon eine utm_source, bleibt der Link voellig
    // unberuehrt - es wird KEIN Parameter ergaenzt, geaendert oder entfernt.
    if (hasExistingUtmSource(rawHref, baseHref)) return false;
    var attribution = getPassthroughAttribution(sessionStore);
    if (!hasAny(attribution)) return false;
    anchor.setAttribute('href', appendParamsToUrl(rawHref, attribution, baseHref));
    return true;
  }

  /**
   * Wendet die SESSION-Attribution auf alle [data-utm-passthrough]
   * Formulare UND Links einer Seite an. Formulare bekommen hidden inputs,
   * Links bekommen die Parameter an ihre href angehaengt. Ist die Session
   * leer (kein aktueller Touchpoint), passiert nichts - bestehende hrefs
   * bleiben unveraendert (das ist der Fix fuer das dauerhafte "Kleben"
   * alter UTM-Parameter am Header-CTA-Button).
   */
  function applyToDocument(doc, localStore, sessionStore, loc, now) {
    capture({ localStorage: localStore, sessionStorage: sessionStore, search: loc.search, href: loc.href, now: now });
    var attribution = getPassthroughAttribution(sessionStore);
    if (!hasAny(attribution)) return attribution;

    var elements = doc.querySelectorAll('[data-utm-passthrough]');
    elements.forEach(function (el) {
      var tag = el.tagName;
      if (tag === 'FORM') {
        Object.keys(attribution).forEach(function (key) {
          var input = el.querySelector('input[name="' + key + '"]');
          if (!input) {
            input = doc.createElement('input');
            input.type = 'hidden';
            input.name = key;
            el.appendChild(input);
          }
          input.value = attribution[key];
        });
      } else if (tag === 'A' && el.getAttribute('href')) {
        // Bereits eigenstaendig getaggte Links (nicht-leere utm_source, z.B.
        // Affiliate) bleiben unberuehrt - gleiche Regel wie bei der automatischen
        // Berater-Erkennung. Sonst entstuende eine Mischattribution aus fremder
        // utm_source und Meta-IDs der Session.
        if (hasExistingUtmSource(el.getAttribute('href'), loc.href)) return;
        el.setAttribute('href', appendParamsToUrl(el.getAttribute('href'), attribution, loc.href));
      }
    });
    return attribution;
  }

  function autoRun() {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    var run = function () {
      applyToDocument(document, window.localStorage, window.sessionStorage, window.location);
    };
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', run);
    } else {
      run();
    }

    // Delegierter Handler fuer generische Berater-Links ohne Attribut. Capture-
    // Phase, damit die href vor allen anderen Klick-Handlern steht. 'auxclick'
    // deckt Mittelklick / "in neuem Tab oeffnen" mit ab.
    var onAnchorActivate = function (event) {
      var target = event.target;
      var anchor = target && target.closest ? target.closest('a[href]') : null;
      if (!anchor) return;
      decorateGenericBeraterAnchor(anchor, window.sessionStorage, window.location);
    };
    document.addEventListener('click', onAnchorActivate, true);
    document.addEventListener('auxclick', onAnchorActivate, true);
  }

  // Im Browser automatisch starten
  if (typeof window !== 'undefined') {
    autoRun();
  }

  return {
    UTM_KEYS: UTM_KEYS,
    BACKGROUND_MAX_AGE_MS: BACKGROUND_MAX_AGE_MS,
    isExpired: isExpired,
    parseIncomingUTM: parseIncomingUTM,
    PASSTHROUGH_KEYS: PASSTHROUGH_KEYS,
    GENERIC_BERATER_PATH: GENERIC_BERATER_PATH,
    STORE_HOSTNAMES: STORE_HOSTNAMES,
    isStoreHostname: isStoreHostname,
    hasExistingUtmSource: hasExistingUtmSource,
    capture: capture,
    getAttribution: getAttribution,
    getSessionAttribution: getSessionAttribution,
    readHeyflowSession: readHeyflowSession,
    getPassthroughAttribution: getPassthroughAttribution,
    isGenericBeraterHref: isGenericBeraterHref,
    decorateGenericBeraterAnchor: decorateGenericBeraterAnchor,
    clearAttribution: clearAttribution,
    clearSession: clearSession,
    appendParamsToUrl: appendParamsToUrl,
    applyToDocument: applyToDocument,
    autoRun: autoRun
  };
}));
